import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, ErrorAlert, Field, Input, Logo, useDocumentTitle } from "@unizpay/ui";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api } from "../lib/api";

/** Only paths on this site: never send a signed-in admin to another origin. */
function safeNext(next: string | null): string {
  return next?.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  useDocumentTitle("Sign in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const login = useMutation({
    mutationFn: () => api.login(email.trim(), password),
    onSuccess: () => {
      queryClient.clear();
      void navigate(safeNext(params.get("next")), { replace: true });
    },
    onError: () => setPassword(""),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    login.mutate();
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 py-10">
      <div className="mb-8 flex items-center gap-2">
        <Logo className="h-8" />
        <span className="rounded-md bg-fg px-1.5 py-0.5 text-[0.6875rem] font-semibold tracking-wide text-surface uppercase">
          Admin
        </span>
      </div>
      <main className="w-full max-w-sm rounded-lg border border-line bg-surface px-6 py-7 shadow-sm">
        <h1 className="text-lg font-semibold">Sign in to the admin panel</h1>
        <p className="mt-1 text-[0.8125rem] text-fg-muted">For UnizPay staff only.</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Field label="Email">
            {(p) => (
              <Input
                {...p}
                type="email"
                name="email"
                autoComplete="username"
                required
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Password">
            {(p) => (
              <Input
                {...p}
                type="password"
                name="password"
                autoComplete="current-password"
                required
                maxLength={200}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <ErrorAlert error={login.error} />
          <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
            Sign in
          </Button>
        </form>
      </main>
    </div>
  );
}
